import cn from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ProgressBar } from 'react-step-progress-bar';

import { CssTransitionItems } from '@webapp/common/components/css-transition-items/css-transition-items';
import { CustomStylesCtx } from '@webapp/common/components/custom-styles';
import { CssQuestionType } from '@webapp/common/components/survey-custom';
import { calcPercent } from '@webapp/common/lib/ui';
import type { PairComparisonParam } from '@webapp/common/resources/entities/question';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';
import { selectInfo } from '@webapp/survey/src/machine/selectors';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import { PairComparisonAnswer } from './pair-comparison-answer';

import css from './pair-comparison.css';

export const PairComparison: FC<SurveyQuestion> = ({ question, readOnly }) => {
    const {
        responseActor,
        answersActors,
        params: { pairComparisonType },
        pairComparisonParams,
    } = question;
    const {
        params: {
            alert: { pairComparisonPairsText, pairComparisonCompleteText },
        },
        design: {
            fonts: { elementColor },
        },
    } = useStateSelector(selectInfo);
    const { fieldBorderColor, fieldBorderColor50 } = useContext(CustomStylesCtx);
    const styles = { fieldBorderColor, fieldBorderColor50 };
    const [activePairIndex, setActivePairIndex] = useState<number>(0);
    const [answeredData, setAnsweredData] = useState<Record<number, number>>(
        // @ts-ignore
        responseActor.getSnapshot().context.value || {},
    );
    const timer = useRef(null);

    const answers = useMemo(() => answersActors.map((a) => a.getSnapshot()), [answersActors]);

    // Индикатор показывает количество отвеченных пар, а не индекс текущей
    const answeredPairsCount = Object.keys(answeredData).length;

    const getPercent = useCallback(
        () => calcPercent(answeredPairsCount, pairComparisonParams?.length),
        [answeredPairsCount, pairComparisonParams?.length],
    );

    useEffect(() => {
        return () => {
            clearTimeout(timer.current);
        };
    }, []);

    const onChange = (id: number, pairNumber: number) => {
        setAnsweredData((prev) => {
            const newResult = { ...prev, [pairNumber]: id };

            if (pairComparisonParams[activePairIndex + 1]) {
                timer.current = setTimeout(() => {
                    setActivePairIndex(activePairIndex + 1);
                }, 200);
            } else {
                responseActor.send({ type: 'CHANGE', value: newResult as unknown as string });
                answersActors[0]
                    .getSnapshot()
                    .context.response.send({ type: 'CHANGE', value: newResult as unknown as string });
            }

            return newResult;
        });
    };

    const renderContent = () => {
        if (readOnly) {
            return (
                <div className={css.pairComparison}>
                    {answers.map((answer) => {
                        return (
                            <PairComparisonAnswer
                                imgPath={answer.context.file?.path}
                                textBlock={answer.context.textBlock}
                                key={answer.context.id}
                                type={pairComparisonType}
                                readOnly={readOnly}
                            />
                        );
                    })}
                </div>
            );
        }

        const item: PairComparisonParam = pairComparisonParams[activePairIndex];

        if (!item) {
            return null;
        }

        return (
            <>
                <div className={css.progressBarContainer}>
                    <div>{pairComparisonPairsText}</div>
                    <div className={css.pairNumber}>
                        {answeredPairsCount}/{pairComparisonParams?.length}
                    </div>
                    <div className={css.progressBar}>
                        <ProgressBar percent={getPercent()} unfilledBackground='#ccc' filledBackground={elementColor} />
                    </div>
                    {answeredPairsCount === pairComparisonParams?.length && <div>{pairComparisonCompleteText}</div>}
                </div>
                <CssTransitionItems type='fade' items={pairComparisonParams} activeItem={activePairIndex}>
                    <div className={css.pairComparisonSurvey}>
                        <PairComparisonAnswer
                            id={item.answer1_id}
                            imgPath={item.answer1_filePath}
                            textBlock={item.answer1_textBlock}
                            type={pairComparisonType}
                            onChange={onChange}
                            selected={answeredData[item.pairNumber] === item.answer1_id}
                            key={`${item.pairNumber}-${item.answer1_id}`}
                            pairNumber={item.pairNumber}
                            styles={styles}
                        />
                        <span className={css.pairComparisonDivider}>VS</span>
                        <PairComparisonAnswer
                            id={item.answer2_id}
                            imgPath={item.answer2_filePath}
                            textBlock={item.answer2_textBlock}
                            type={pairComparisonType}
                            onChange={onChange}
                            selected={answeredData[item.pairNumber] === item.answer2_id}
                            key={`${item.pairNumber}-${item.answer2_id}`}
                            pairNumber={item.pairNumber}
                            styles={styles}
                        />
                    </div>
                </CssTransitionItems>
            </>
        );
    };

    return (
        <div className={cn(CssQuestionType.QUESTION, CssQuestionType.PAIR_COMPARISON, css.wrap)}>{renderContent()}</div>
    );
};
